import React, {
    useContext, useEffect, useMemo, useState,
} from 'react';
import moment from 'moment';
import { useCookies } from 'react-cookie';
import { useTranslation } from 'react-i18next';
import {
    useLoaderData,
    useLocation,
    useNavigate,
    useParams,
    useSearchParams,
} from 'react-router-dom';
import { useImmer } from 'use-immer';

import { PaymentProcessor } from '@r6digital/r6pay-types/types/PaymentProcessor';
import { NextAction } from '@r6digital/r6pay-types/types/Requests/PaymentLinkCreate';
import {
    creditCardType,
    validateCreditCardCSV,
    validateCreditCardNumber,
} from '@r6digital/rapidstor-types/react/entities/CreditCard';
import { WizardType } from '@r6digital/rapidstor-types/react/api/Requests';
import Descriptor from '@r6digital/rapidstor-types/react/entities/Descriptor';
import { ScaApplicationViews } from '@r6digital/rapidstor-types/enums/sockets';
import Location from '@r6digital/rapidstor-types/react/entities/Location';
import UnitType from '@r6digital/rapidstor-types/react/entities/UnitType';
import { BaseReservation } from '@r6digital/rapidstor-types/types/Reservation';

import {
    useGetChargesQuery,
    useGetReservationQuery,
    useGetWizardInitialisationQuery,
    usePostReservationFeeMutation,
    usePostScaReservationWithFeePrepareMutation,
} from '@/core/api/rapidstor';
import { WizardValidationError } from '@/core/types/Wizard';
import Logger from '@/core/utils/logging';
import { isReservationFeeConfirmationStep, RapidStorSettings } from '@/core/utils/rapidstor';
import { getBackDestination } from '@/core/utils/routing';
import { normaliseFieldName, validateString } from '@/core/utils/validation';

import { useReservationWithFeeUnitAvailability } from '@/core/hooks/useUnitAvailability';

import store from '@/store';
import { setUnitData } from '@/store/bookingSlice';

import Alert from '@/core/components/elements/alert';
import Button from '@/core/components/elements/button';
import ReservationUnavailable from '@/core/components/elements/reservation-unavailable';
import Spinner from '@/core/components/elements/spinner';
import JourneyProgress from '@/core/components/journey-progress';
import WizardDescriptor from '@/core/components/wizard/descriptor';
import PaymentForm from '@/core/components/wizard/forms/payment-form';
import StripePaymentForm from '@/core/components/wizard/forms/stripe-payment-form';
import WizardPricingTable from '@/core/components/wizard/pricing-table';

import { paymentGatewayReturnUrl, transformCostRetrieve } from '@/features/rapidstor/helpers';
import { JourneyContext } from '@/features/rapidstor/providers/JourneyProvider';
import { useR6PayContext } from '@/features/rapidstor/providers/R6PayServiceProvider';
import { StripePaymentIntent } from '@/features/rapidstor/services/R6PayService';

import classes from '@/scss/screens/reservation-with-fee.module.scss';

// Carries the amount the intent was created for, so a fee that changes while the
// payment link request is in flight can invalidate the intent it comes back with.
type PendingStripeIntent = StripePaymentIntent & { amount: number };

export default function ReservationWithFee() {
    const location = useLoaderData() as Location;
    const navigate = useNavigate();
    const { token } = useParams();
    const [searchParams] = useSearchParams();
    const { t } = useTranslation();
    const {
        unitList,
    } = useContext(JourneyContext);
    const r6PayService = useR6PayContext(location);
    const error = searchParams.get('error');
    const errorMessage = searchParams.get('message');

    const [descriptor, setDescriptor] = useState<Descriptor>();
    const [unitType, setUnitType] = useState<UnitType>();
    const [reservation, setReservation] = useState<BaseReservation>();
    const [submittingPayment, setSubmittingPayment] = useState<boolean>(false);
    const [validationErrors, setValidationErrors] = useState<
    WizardValidationError[]
    >([]);
    const [addFeeError, setAddFeeError] = useState<string>();
    const [stripeIntent, setStripeIntent] = useState<PendingStripeIntent | null>(null);

    const [prepareScaReservationWithFee] = usePostScaReservationWithFeePrepareMutation();
    const [updateReservationWithFee] = usePostReservationFeeMutation();

    const {
        data: res,
        isSuccess: onSuccess,
        error: reservationError,
    } = useGetReservationQuery(
        {
            locationCode: location.locationCode,
            corpCode: location.corpCode,
            token,
        },
        {
            refetchOnMountOrArgChange: reservation?.token !== token,
        },
    );

    const { data: charges, error: chargesError } = useGetChargesQuery(
        {
            locationCode: location.locationCode,
            corpCode: location.corpCode,
        },
        {
            skip: !onSuccess,
        },
    );

    const { data: wizardInit, error: wizardInitError } = useGetWizardInitialisationQuery(
        {
            locationCode: location.locationCode,
            corpCode: location.corpCode,
            wizard: WizardType.RESERVATION_WITH_FEE,
            reservationToken: token,
        },
        {
            skip: !onSuccess,
            refetchOnMountOrArgChange: true,
        },
    );

    // SiteLink state of the reservation, read on page load. SiteLink accepts a fee against
    // a cancelled reservation, so the form must never be shown for one.
    const blockingStatus = wizardInit?.reservationStatus
        && (wizardInit.reservationStatus.cancelled || wizardInit.reservationStatus.expired)
        ? wizardInit.reservationStatus
        : null;

    // Until init answers, the status is unknown. Rendering the payment form in that window
    // would let a customer start paying on a reservation the response is about to block.
    // The reservation error is its own branch below, so waiting stops when that call fails.
    const wizardInitPending = !wizardInit && !wizardInitError && !reservationError;

        // Use shared unit availability hook
    const { handleUnitAvailability } = useReservationWithFeeUnitAvailability(
        location,
        res,
        setReservation,
        setAddFeeError,
        setSubmittingPayment,
    );

    const [form, updateForm] = useImmer<Record<string, string>>({
        cardHolderName: '',
        cardNumber: '',
        cardExpiryMonth: '',
        cardExpiryYear: '',
        cardCsv: '',
    });

    const [, setCookie] = useCookies(['reservation']);

    const updateExpiryDate = (value: Record<string, string | number>) => {
        if (!value.floatValue) {
            updateForm((draft) => {
                draft.cardExpiryMonth = '';
                draft.cardExpiryYear = '';
                return draft;
            });
        } else {
            updateForm((draft) => {
                const [month, year] = String(value.formattedValue).split('/');
                draft.cardExpiryMonth = month;
                draft.cardExpiryYear = year;
                return draft;
            });
        }
    };

    const update = (key: string, event: React.BaseSyntheticEvent) => {
        updateForm((draft) => {
            if (key === 'cardNumber') {
                const value = String(event.target.value).replace(' ', '');
                draft[key] = value;
            } else {
                draft[key] = String(event.target.value);
            }
            return draft;
        });
    };

    const setupSliceData = () => {
        // Set UnitData
        if (descriptor && unitType && unitList) {
            store.dispatch(
                setUnitData({
                    location,
                    unitType,
                    descriptor,
                    descriptors: unitList.descriptors,
                }),
            );
        }
    };

    const checkCookie = () => {
        if (reservation) {
            // Set Cookie
            setCookie(
                'reservation',
                {
                    token,
                    id: reservation._id,
                },
                {
                    path: window.location.pathname,
                    maxAge: 60 * 60 * 24 * 7,
                },
            );
            setupSliceData();
        }
    };

    const initiateR6Pay = async () => {
        await r6PayService.service?.getPaymentMethods();
    };

    useEffect(() => {
        setReservation(res);
    }, [onSuccess, res]);

    useEffect(() => {
        if (!token || !window.rapidstor_settings.rapidstor_options.corpToken) {
            navigate('/');
        } else {
            setReservation(res);
            checkCookie();

            if (
                location.rapidstor.paymentGateways.use
        && location.rapidstor.paymentGateways.gateways.r6Pay
        && !r6PayService.service
            ) {
                initiateR6Pay();
            }
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (reservation && unitList) {
            unitList.descriptors.forEach((d: Descriptor) => {
                const found = d.unitTypes.find((uT: UnitType) => uT._id === reservation.unitType._id);
                if (found) {
                    setUnitType(found);
                    setDescriptor(d);
                    checkCookie();
                }
            });
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [unitList, reservation]);

    const totalCost = useMemo(() => {
        if (!charges) return 0;
        return transformCostRetrieve(charges, undefined, undefined, 0).reduce(
            (acc, item) => acc + item.chargeAmount + item.taxAmount - item.discountAmount,
            0,
        );
    }, [charges]);

    const isStep2 = useMemo(
        () => isReservationFeeConfirmationStep(window.location.hash, res),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [window.location.hash, res],
    );

    // A Stripe intent is created for a fixed amount, so drop it if the fee changes
    // underneath it (charges reloading) and let the customer start the payment again.
    // Depending on the intent as well as the fee catches the request that was already
    // in flight when the fee changed: its response arrives after this effect last ran,
    // and confirming it would charge the customer the old amount.
    useEffect(() => {
        if (stripeIntent && stripeIntent.amount !== totalCost) {
            setStripeIntent(null);
        }
    }, [totalCost, stripeIntent]);

    useEffect(() => {
        if (isStep2) {
            document.getElementById('confirmation-panel')?.scrollIntoView({
                behavior: 'smooth',
                block: 'center',
            });
        }
    }, [isStep2]);

    const validateForm = (): WizardValidationError[] => {
        setValidationErrors([]);
        const errors: WizardValidationError[] = [];

        const formKeys = Object.keys(form);

        const usingPaymentGateway = location.rapidstor.paymentGateways.use
            && location.rapidstor.paymentGateways.gateways.r6Pay;

        if (usingPaymentGateway) {
            setValidationErrors([]);
            return [];
        }

        for (const key of formKeys) {
            const normalisedKey = normaliseFieldName(key);

            if (!validateString(form[key])) {
                errors.push({
                    name: key,
                    message: `${normalisedKey} is required`,
                });
            }
        }

        setValidationErrors(errors);

        return errors;
    };

    const payViaGateway = async (payment_method: PaymentProcessor) => {
        if (
            location.rapidstor.paymentGateways.use
            && location.rapidstor.paymentGateways.gateways.r6Pay
            && reservation
        ) {
            const returnUrl = paymentGatewayReturnUrl(payment_method, reservation.token, 'reservation-with-fee');

            const payload = r6PayService.service.buildCreatePaymentLinkPayload({
                reservation,
                location,
                currency: location.rapidstor.paymentGateways.gateways.r6Pay.currency,
                returnUrl,
                paymentMethod: payment_method,
                reservationFee: totalCost,
            });

            try {
                const result = await r6PayService.service.createPaymentLink(payload);
                if (!result) {
                    Logger.error('[processBooking] Failed R6Pay request', location, [
                        {
                            name: 'rapidstor_reservationToken',
                            value: `${res?.token}`,
                        },
                    ]);
                    setSubmittingPayment(false);
                    setAddFeeError('R6Pay Create Payment Error');
                    return;
                }
                if (result.next_action.type === NextAction.REDIRECT_TO_URL) {
                    window.location.href = result.next_action.url;
                } else if (result.next_action?.type === NextAction.POST_TO_URL) {
                    r6PayService.service.postToUrl(
                        result.next_action.url,
                        result.next_action.form_data || {},
                    );
                } else if (result.next_action?.type === NextAction.USE_STRIPE_SDK) {
                    setStripeIntent({
                        clientSecret: result.next_action.client_secret,
                        publishableKey: result.next_action.publishable_key,
                        paymentLinkId: result.payment_link_id,
                        returnUrl,
                        amount: totalCost,
                    });
                    setSubmittingPayment(false);
                }
            } catch (err) {
                Logger.error(
                    `[processBooking] Failed R6Pay request: ${JSON.stringify(err)}`,
                    location,
                    [
                        {
                            name: 'rapidstor_reservationToken',
                            value: `${res?.token}`,
                        },
                    ],
                );
                setSubmittingPayment(false);
                setAddFeeError('R6Pay Create Payment Error');
            }
        }
    };

    const submitPayment = async (
        payment_method?: PaymentProcessor,
        e?: React.FormEvent<HTMLFormElement>,
    ) => {
        if (e) {
            e.preventDefault();
        }
        setSubmittingPayment(true);
        setAddFeeError(undefined);

        const errors = validateForm();

        if (errors.length) {
            setSubmittingPayment(false);
            return;
        }

        const availability = await handleUnitAvailability();

        if (!availability) {
            setSubmittingPayment(false);
            return;
        }

        if (payment_method) {
            await payViaGateway(payment_method);
        } else {
            const cc = creditCardType(form.cardNumber);

            if (!validateCreditCardNumber(form.cardNumber, location) || !cc) {
                errors.push({
                    name: 'cardNumber',
                    message: 'Invalid card number',
                });
                setValidationErrors(errors);
                setSubmittingPayment(false);
                return;
            }

            const formattedExpiryMonth = moment(form.cardExpiryMonth, 'MM').format(
                'MM',
            );
            const formattedExpiryYear = moment(form.cardExpiryYear, 'YYYY').format(
                'YYYY',
            );
            const lastDayOfMonth = moment(
                `${formattedExpiryYear}-${formattedExpiryMonth}`,
            ).endOf('month');
            const today = moment();

            if (lastDayOfMonth < today) {
                errors.push({
                    name: 'cardExpiry',
                    message: 'Card has expired',
                });
            }

            if (cc && !validateCreditCardCSV(cc, form.cardCsv)) {
                errors.push({
                    name: 'cardCsv',
                    message: 'Invalid CSV',
                });
            }

            if (errors.length > 0) {
                setValidationErrors(errors);
                setSubmittingPayment(false);
                return;
            }
            setValidationErrors([]);

            if (!reservation?.globalWaitingNum || !reservation._id) {
                setSubmittingPayment(false);
                setAddFeeError(
                    'An error occured while processing the reservation, please try again or contact support.',
                );
                return;
            }
            if (location.scaConfiguration && Object.keys(location.scaConfiguration).length > 0) {
                const {
                    href, protocol, host, pathname, search,
                } = window.location;

                try {
                    const result = await prepareScaReservationWithFee({
                        locationCode: location.locationCode,
                        corpCode: location.corpCode,
                        payload: {
                            locationCode: location.locationCode,
                            cardCVV: form.cardCsv,
                            cardExpiryMonth: form.cardExpiryMonth,
                            cardExpiryYear: form.cardExpiryYear,
                            cardHoldersName: form.cardHolderName,
                            cardNumber: form.cardNumber,
                            chargeTotal: totalCost,
                            cardTypeId: cc,
                            view: ScaApplicationViews.standalone,
                            waitingNum: reservation.globalWaitingNum,
                            reservationId: reservation._id,
                            // eslint-disable-next-line max-len
                            standaloneReturnUrl:
        `${window.location.protocol}//${window.location.host}${window.location.pathname}${window.location.search}#/api/payment-redirect/${reservation.token}/reservation-with-fee`,
                            frontendUrls: {
                                original: href,
                                success: `${protocol}//${host}${pathname}${search}#/api/payment-redirect/${res?.token}/reservation-with-fee`,
                                fail: `${protocol}//${host}${pathname}${search}#/api/payment-redirect/${res?.token}/reservation-with-fee`,
                            },
                        },
                    }).unwrap();

                    if (result?.redirect) {
                        Logger.info('[submitPayment] Redirecting to SCA payment URL');
                        window.location.href = result.redirect;
                        setSubmittingPayment(false);
                    } else {
                        Logger.warn(
                            'An error occued while preparing SCA payment',
                            location,
                            [
                                {
                                    name: 'result',
                                    value: JSON.stringify(result),
                                },
                            ],
                        );
                        setAddFeeError(
                            'An error occured while preparing SCA payment, please try again or contact support.',
                        );
                        setSubmittingPayment(false);
                    }
                } catch (err) {
                    Logger.error(
                        `An error occured while processing a reservation with fee: ${JSON.stringify(
                            err,
                        )}`,
                        location,
                    );
                    setAddFeeError(`${err}`);
                    setSubmittingPayment(false);
                }
            } else {
                try {
                    const result = await updateReservationWithFee({
                        locationCode: location.locationCode,
                        corpCode: location.corpCode,
                        token: reservation._id,
                        payload: {
                            card: {
                                cvv: form.cardCsv,
                                expiryYear: form.cardExpiryYear,
                                expiryMonth: form.cardExpiryMonth,
                                name: form.cardHolderName,
                                number: form.cardNumber,
                                type: cc,
                            },
                            reservationId: reservation._id,
                        },
                    }).unwrap();

                    setSubmittingPayment(false);
                    Logger.info('[submitPayment] Payment successful');

                    navigate(
                        `/api/payment-redirect/${result.token}/reservation-with-fee`,
                    );
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                } catch (err: any) {
                    const message = err?.data?.error || err?.message || error || JSON.stringify(err);

                    Logger.error(
                        `An error occured while processing a reservation with fee: ${message}`,
                        location,
                    );
                    setAddFeeError(`${message}`);
                    setSubmittingPayment(false);
                }
            }
        }
    };

    // Without a payment gateway the customer types their card into our own form;
    // with one they pick a gateway payment method instead.
    const showCardForm = RapidStorSettings(location).paymentForm().show();

    const windowLocation = useLocation();

    const goBack = () => {
        navigate(getBackDestination(windowLocation.pathname));
    };

    const confirmBookingButtonText = (confirmText?: string): string => t(`wizard:${confirmText || 'next'}`);

    useEffect(() => {
        if (error || errorMessage) {
            setAddFeeError(`${errorMessage}`);
        }
    }, [error, errorMessage]);

    if (wizardInitPending) {
        return <Spinner size="80" />;
    }

    if (blockingStatus) {
        return (
            <ReservationUnavailable
                status={blockingStatus}
                onGoBack={() => navigate('/')}
            />
        );
    }

    return (
        <div
            className={`${classes.container} ${
                submittingPayment && classes.disabled
            }`}
        >
            <div className={`${classes.header}`}>
                <div>
                    {!isStep2 && (
                        <Button
                            type="primaryOnWhite"
                            size="medium"
                            chevron="left"
                            onclick={goBack}
                        >
                            <span>{t('wizard:back')}</span>
                        </Button>
                    )}
                </div>
                <span data-testid="reservation-title" className={`${classes.title}`}>{t('wizard:reservation')}</span>
                <div />
            </div>
            <JourneyProgress
                step={isStep2 ? 2 : 1}
                stepsType="reservation-with-fee"
            />

            {reservationError || chargesError || wizardInitError ? (
                <div id="error-page" className={classes.error_page}>
                    <span className={classes.header}>{t('error:oops')}</span>
                    <p className={classes.p}>{t('error:subtitle')}</p>
                    <p className={classes.p1}>{t('error:general_error')}</p>
                    <Button onclick={() => navigate('/')} type="primary" size="medium">
                        {t('error:go_back')}
                    </Button>
                </div>
            ) : (
                <>
                    {isStep2 && (
                        <div className={`${classes.confirmation}`} id="confirmation-panel">
                            {error ? (
                                <div id="error-page" className={classes.error_page}>
                                    <span className={classes.header}>{t('error:oops')}</span>
                                    <p className={classes.p}>{t('error:subtitle')}</p>
                                    <p className={classes.p1}>{errorMessage}</p>
                                    <Button
                                        onclick={() => navigate('/')}
                                        type="primary"
                                        size="medium"
                                    >
                                        {t('error:go_back')}
                                    </Button>
                                </div>
                            ) : (
                                <div className={`${classes.success}`}>
                                    <div className={`${classes.top_container}`}>
                                        <span className={`${classes.orderNumber}`}>
                                            {t('wizard:reservationWithFee:orderNumber')}
                                        </span>
                                        <span className={`${classes.waitingNum}`}>
                                            {reservation?.globalWaitingNum}
                                        </span>
                                    </div>
                                    <span className={`${classes.confirmation_text}`}>
                                        {t('wizard:reservationWithFee:confirmationText')}
                                    </span>
                                </div>
                            )}
                        </div>
                    )}

                    {isStep2 && !error && <div className={`${classes.fold}`} />}

                    {!error && (
                        <WizardDescriptor
                            descriptor={descriptor}
                            unitType={unitType}
                            deal={reservation?.deal ?? undefined}
                        />
                    )}

                    {!isStep2 && <div className={`${classes.fold}`} />}

                    {reservation && !isStep2 && (
                        <span className={classes.copy}>{t('wizard:copy')}</span>
                    )}

                    {!isStep2 && (
                        <>
                            <WizardPricingTable
                                costRetrieve={undefined}
                                charges={charges}
                                merchandiseDiscount={0}
                            />

                            <div
                                className={`${classes.payment_container}`}
                                hidden={!descriptor || !charges}
                            >
                                <span className={`${classes.header}`}>
                                    {t('journey_bar:step_5')}
                                </span>
                                {addFeeError && <Alert type="error" text={addFeeError} />}

                                {showCardForm ? (
                                    <>
                                        {submittingPayment ? (<Spinner size="80" />) : null}
                                        {!submittingPayment ? (
                                            <PaymentForm
                                                onSubmit={(e) => submitPayment(undefined, e)}
                                                form={form}
                                                submittingPayment={submittingPayment}
                                                update={update}
                                                updateExpiryDate={updateExpiryDate}
                                                validationErrors={validationErrors}
                                            />
                                        ) : null}
                                    </>
                                ) : null}

                                {/* Outside .payViaGateway, whose narrow cap suits the gateway
                                    buttons but squashes the Stripe payment element. */}
                                {!showCardForm && stripeIntent && (
                                    <StripePaymentForm
                                        intent={stripeIntent}
                                        onError={(msg) => setAddFeeError(msg)}
                                        buttonText="Pay Now"
                                    />
                                )}

                                {!showCardForm && !stripeIntent && (
                                    <div className={`${classes.payViaGateway}`}>
                                        {r6PayService.service?.paymentMethods?.map((method) => (
                                            <Button
                                                key={`payment-method-idx-${method.id}`}
                                                disableScroll
                                                onclick={() => {
                                                    submitPayment(method.type);
                                                }}
                                                type="primary"
                                                size="medium"
                                                disabled={submittingPayment}
                                                style={{ marginBottom: '12px' }}
                                            >
                                                {submittingPayment ? (
                                                    <Spinner size="25" inverted />
                                                ) : (
                                                    <>
                                                        {confirmBookingButtonText(
                                                            method.custom_fields?.display_name,
                                                        )}
                                                    </>
                                                )}
                                            </Button>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </>
                    )}
                </>
            )}
        </div>
    );
}
